import * as Sentry from '@sentry/react'
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import Login from '@/pages/Login';
import SetPassword from '@/pages/SetPassword';
import PublicPortfolio from '@/pages/PublicPortfolio';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const PUBLIC_PAGES = ["AplicacaoParceiros"];

const NoSubscriptionGate = () => {
  const { user, logout } = useAuth();
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #ffffff 0%, #edfbf4 55%, #f5fef9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: '100%', maxWidth: 440, margin: '0 16px',
        background: '#fff', borderRadius: 24, padding: '44px 40px',
        boxShadow: '0 8px 48px 0 rgba(0,102,76,0.10), 0 1px 0 0 rgba(0,220,164,0.15)',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 28 }}>
          <img src="/logo.png" alt="Portüs Talent Pro" style={{ height: 52, width: 'auto', margin: '0 auto' }} />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#003326', marginBottom: 8, fontFamily: 'Poppins, sans-serif' }}>
          Assinatura não encontrada
        </h1>
        <p style={{ fontSize: 14, color: '#646C6F', lineHeight: 1.6, marginBottom: 28 }}>
          Olá{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! Para acessar o Portüs Talent Pro você precisa de uma assinatura ativa.
        </p>
        <a
          href="https://seja.portusdigital.pro"
          style={{
            display: 'inline-block', width: '100%', padding: '14px',
            background: '#00DCA4', color: '#003326',
            borderRadius: 99, fontSize: 15, fontWeight: 700,
            textDecoration: 'none', textAlign: 'center',
          }}
        >
          Quero assinar o Talent Pro
        </a>
        <button
          onClick={logout}
          style={{
            display: 'block', width: '100%', marginTop: 12, padding: '12px',
            background: 'transparent', color: '#99A6AB',
            borderRadius: 99, fontSize: 13, fontWeight: 600,
            border: 'none', cursor: 'pointer',
          }}
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isAuthenticated, user } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if authenticated user has active Pro subscription (admins are exempt)
  const isAdmin = user?.role === 'admin';
  const hasPro = !!user?.subscription_started_at;
  const needsSubscription = isAuthenticated && !hasPro && !isAdmin;

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/definir-senha" element={<SetPassword />} />

      {/* Public routes (no auth required) */}
      {Object.entries(Pages)
        .filter(([path]) => PUBLIC_PAGES.includes(path))
        .map(([path, Page]) => (
          <Route key={path} path={`/${path}`} element={<Page />} />
        ))}

      <Route path="/" element={
        isAuthenticated
          ? needsSubscription
            ? <NoSubscriptionGate />
            : <LayoutWrapper currentPageName={mainPageKey}><MainPage /></LayoutWrapper>
          : <Navigate to="/login" replace />
      } />
      {Object.entries(Pages)
        .filter(([path]) => !PUBLIC_PAGES.includes(path))
        .map(([path, Page]) => (
          <Route
            key={path}
            path={`/${path}`}
            element={
              isAuthenticated
                ? needsSubscription
                  ? <NoSubscriptionGate />
                  : <LayoutWrapper currentPageName={path}><Page /></LayoutWrapper>
                : <Navigate to="/login" replace />
            }
          />
        ))}
      <Route path="/:slug" element={<PublicPortfolio />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const ErrorFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F9FAFA',
  }}>
    <div style={{
      textAlign: 'center',
      maxWidth: 400,
      padding: '40px 24px',
    }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#003326', marginBottom: 8, fontFamily: 'Poppins, sans-serif' }}>
        Algo deu errado
      </h1>
      <p style={{ fontSize: 14, color: '#646C6F', lineHeight: 1.6, marginBottom: 24 }}>
        Ocorreu um erro inesperado. Nossa equipe foi notificada automaticamente.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '12px 32px',
          background: '#00DCA4',
          color: '#003326',
          borderRadius: 99,
          fontSize: 14,
          fontWeight: 700,
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Recarregar
      </button>
    </div>
  </div>
);

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <NavigationTracker />
            <AuthenticatedApp />
          </Router>
          <Toaster />
        </QueryClientProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
